import React from 'react';

interface LimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  limitType?: 'daily' | 'file_size' | 'pages';
  details?: string;
}

const LimitReachedModal: React.FC<LimitReachedModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  limitType = 'daily',
  details,
}) => {
  if (!isOpen) return null;

  const getMessage = () => {
    switch (limitType) {
      case 'file_size':
        return {
          title: 'File Too Large',
          body: details || 'This file exceeds the size limit for free accounts.',
          subtext: 'Upgrade to Pro for files up to 50 MB.',
        };
      case 'pages':
        return {
          title: 'Too Many Pages',
          body: details || 'This PDF exceeds the page limit for free accounts.',
          subtext: 'Upgrade to Pro for PDFs with up to 100 pages.',
        };
      default:
        return {
          title: 'Daily Limit Reached',
          body: "You've used all 5 free document preparations for today.",
          subtext: 'Upgrade for unlimited preparation and no watermark.',
        };
    }
  };

  const message = getMessage();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Icon */}
        <div className="flex justify-center pt-8">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-amber-600 dark:text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 text-center">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
            {message.title}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-1">
            {message.body}
          </p>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            {message.subtext}
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 space-y-3">
          <button
            onClick={onUpgrade}
            className="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Upgrade Now
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 text-slate-600 dark:text-slate-400 font-medium hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LimitReachedModal;

